import { useRef, useState, useEffect } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";

import { Linkedin, Instagram, RotateCw, ArrowUpRight } from "lucide-react";
import ContactSheet from "@/components/ContactSheet";
import BookingStatus from "@/components/BookingStatus";
import ToolStack from "@/components/ToolStack";
import madiHeadshot from "@/assets/madi-headshot.jpg";

const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? "100%" : "-100%",
    opacity: 0
  }),
  center: {
    x: 0,
    opacity: 1
  },
  exit: (direction: number) => ({
    x: direction > 0 ? "-100%" : "100%",
    opacity: 0
  })
};

const fadeVariants = {
  enter: { opacity: 0, y: 20 },
  center: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 }
};

const playbookQuestions = [
{ number: "01", text: "Where am I?" },
{ number: "02", text: "What can I do here?" },
{ number: "03", text: "How do I do it?" },
{ number: "04", text: "How do I know it worked?" },
{ number: "05", text: "What should I do next?" }];

const PlaybookSubSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <div ref={ref} data-playbook className="mt-8 md:mt-12 pt-8 md:pt-12 border-t border-foreground/10">
      <motion.div
        initial={{ opacity: 0 }}
        animate={isInView ? { opacity: 1 } : {}}
        transition={{ duration: 0.6 }}
        className="mb-6">

        <p className="font-body text-[9px] font-medium tracking-[0.25em] uppercase text-muted-foreground mb-2">
          Our Playbook
        </p>
      </motion.div>

      <motion.h2
        initial={{ opacity: 0, y: 30 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        transition={{ delay: 0.1, duration: 0.7 }}
        className="font-display text-[clamp(2rem,5vw,4rem)] italic leading-[1] text-foreground/90 max-w-2xl mb-8 md:mb-12">

        Remove all guesswork.
      </motion.h2>

      <div className="grid lg:grid-cols-12 gap-y-8 lg:gap-x-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="lg:col-span-5 lg:col-start-1">

          <div className="font-body text-[13px] leading-[2] text-foreground/70 space-y-4">
            <p>Any time someone lands on your website, they're subconsciously trying to orient themselves. They want to know where they are, what this page is about, and what they're supposed to do next.</p>
            <p>If that's unclear within a few seconds, most people won't stick around. Cool visuals grab attention, but clarity earns trust. People come to your site with intent—to buy, book, learn, or explore.</p>
            <p>We structure every page so users know exactly what to do next, and your team knows exactly how to manage it. We strive to ensure no guesswork for your audience and no guesswork internally.</p>
          </div>
          <p className="font-display text-[clamp(1.1rem,2.5vw,1.5rem)] italic leading-[1.3] text-foreground/65 mt-10 pt-6 flex items-center gap-3">
            We make sure your users can answer these questions, fast
            <ArrowUpRight size={18} className="flex-shrink-0 opacity-60 hidden lg:block" />
            <span className="flex-shrink-0 opacity-60 lg:hidden">↓</span>
          </p>
        </motion.div>

        <div className="lg:col-span-5 lg:col-start-8">
          {playbookQuestions.map((q, i) =>
          <motion.div
            key={q.number}
            initial={{ opacity: 0, x: -16 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.3 + i * 0.1, duration: 0.5 }}>
              <motion.div
              initial={{ scaleX: 0 }}
              animate={isInView ? { scaleX: 1 } : {}}
              transition={{ delay: 0.3 + i * 0.1, duration: 0.6, ease: "easeOut" }}
              className="h-px bg-foreground/10 origin-left" />
              <div className="flex items-baseline gap-4 py-5 md:py-6">
                <span className="font-body text-[10px] text-muted-foreground/40 w-6 flex-shrink-0">
                  {q.number}
                </span>
                <p className="font-display text-[clamp(1.25rem,3vw,2rem)] italic text-foreground/80 leading-snug">
                  {q.text}
                </p>
              </div>
            </motion.div>
          )}
          <motion.div
            initial={{ scaleX: 0 }}
            animate={isInView ? { scaleX: 1 } : {}}
            transition={{ delay: 0.8, duration: 0.6, ease: "easeOut" }}
            className="h-px bg-foreground/10 origin-left" />
        </div>
      </div>

    </div>);

};

const AboutSection = () => {
  const ref = useRef(null);
  const sectionRef = useRef<HTMLElement>(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const [direction, setDirection] = useState(1);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 1024);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const goToSlide = (slide: number) => {
    setDirection(slide > activeSlide ? 1 : -1);
    setActiveSlide(slide);
    // Scroll back to top of section on mobile
    if (window.innerWidth < 1024 && sectionRef.current) {
      setTimeout(() => {
        sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  };

  return (
    <section id="about" ref={sectionRef} className="section-dark py-6 md:py-16 lg:py-32 px-5 md:px-10 lg:px-14">
      <div>
      <motion.div
          ref={ref}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="mb-6">

         <p className="font-body text-[9px] font-medium tracking-[0.25em] uppercase text-muted-foreground mb-2">
           05 — Who We Are
         </p>
      </motion.div>

      {/* Mobile tabs */}
      <div className="flex lg:hidden items-center gap-1 mb-8">
        <button
            onClick={() => goToSlide(0)}
            className={`font-body text-ts-2xs font-medium tracking-[0.15em] uppercase px-3.5 py-1.5 border transition-colors duration-300 ${
            activeSlide === 0 ?
            "border-foreground/30 text-foreground bg-foreground/[0.04]" :
            "border-foreground/[0.06] text-muted-foreground hover:text-foreground hover:border-foreground/15"}`
            }>

           The Studio
         </button>
         <button
            onClick={() => goToSlide(1)}
            className={`font-body text-ts-2xs font-medium tracking-[0.15em] uppercase px-3.5 py-1.5 border transition-colors duration-300 ${
            activeSlide === 1 ?
            "border-foreground/30 text-foreground bg-foreground/[0.04]" :
            "border-foreground/[0.06] text-muted-foreground hover:text-foreground hover:border-foreground/15"}`
            }>

           Meet the Founder
         </button>
      </div>

      {/* Content area */}
      <div className="relative">
        {/* Dot indicators — top right */}
        <div className="hidden lg:flex items-center gap-3 absolute top-0 right-0 z-10">
           <button
              onClick={() => goToSlide(activeSlide === 0 ? 1 : 0)}
              className="group flex items-center gap-1.5 font-body text-ts-2xs tracking-[0.15em] uppercase text-muted-foreground hover:text-foreground/70 transition-colors duration-300">

            <RotateCw size={10} className="group-hover:rotate-180 transition-transform duration-500" />
            {activeSlide === 0 ? "Meet the founder" : "Back to studio"}
          </button>
          <span className="w-px h-3 bg-foreground/15" />
          <button
              onClick={() => goToSlide(0)}
              className="w-6 h-6 flex items-center justify-center"
              aria-label="View studio">

            <span className={`w-2.5 h-2.5 rounded-full border transition-all duration-300 ${
              activeSlide === 0 ?
              "bg-foreground/80 border-foreground/80 scale-100" :
              "bg-transparent border-foreground/30 scale-90 hover:border-foreground/50"}`
              } />
          </button>
          <button
              onClick={() => goToSlide(1)}
              className="w-6 h-6 flex items-center justify-center"
              aria-label="Meet the founder">

            <span className={`w-2.5 h-2.5 rounded-full border transition-all duration-300 ${
              activeSlide === 1 ?
              "bg-foreground/80 border-foreground/80 scale-100" :
              "bg-transparent border-foreground/30 scale-90 hover:border-foreground/50"}`
              } />
          </button>
        </div>

        <div className="relative overflow-visible min-h-[auto] lg:min-h-[500px]">
          <AnimatePresence initial={false} custom={direction} mode="wait">
            {activeSlide === 0 ?
              <motion.div
                key="studio"
                custom={direction}
                variants={isMobile ? fadeVariants : slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}>

                {/* ═══ SIDE A: The Studio ═══ */}
                <motion.h2
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.15, duration: 0.7 }}
                  className="font-display text-[clamp(2.5rem,6vw,5rem)] italic leading-[1.15] text-foreground/90 max-w-3xl mb-6 md:mb-16 lg:mb-24">

                  Good design should move people.
                  <span className="text-muted-foreground/60"> Not confuse them.</span>
                </motion.h2>

                <div className="grid lg:grid-cols-12 gap-y-8 lg:gap-y-14 lg:gap-x-8">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3, duration: 0.6 }}
                    className="lg:col-span-5 lg:col-start-1">

                    <div className="font-body text-ts-xs leading-[2] text-muted-foreground space-y-1 [text-wrap:pretty]">
                      <p>Let's be forreal: no one outside of design Twitter cares about how cool a website <em className="font-body italic text-foreground/80">looks</em>. The average person just wants to find what they came for and go.</p>
                      <p>We build no-code websites for the majority of users (sorry design friends).</p>
                      <p>Yes, we know our work looks good, but more importantly, our sites load fast, make sense, and guide people where they're supposed to go. That's it — that's the goal.</p>
                    </div>
                     <ContactSheet>
                       <button
                        className="mt-8 inline-flex items-center gap-2 font-body text-ts-2xs font-medium tracking-[0.15em] uppercase border border-foreground/20 px-6 py-3 text-foreground/70 hover:text-foreground hover:border-foreground/50 transition-all duration-300">

                        Work With Us
                      </button>
                    </ContactSheet>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.4, duration: 0.6 }}
                    className="lg:col-span-4 lg:col-start-8 flex flex-col gap-0">

                    {[
                    { label: "Focus", value: "Web Design · UX/UI · Framer · E-Commerce" },
                    { label: "Based", value: "Dallas, TX" }].
                    map((item) =>
                    <div key={item.label} className="border-t border-foreground/10 py-4">
                         <p className="font-body text-ts-2xs font-semibold tracking-[0.25em] uppercase text-muted-foreground mb-1.5">
                           {item.label}
                         </p>
                         <p className="font-body text-ts-2xs text-muted-foreground tracking-wide">
                           {item.value}
                         </p>
                       </div>
                    )}


                    <div className="border-t border-b border-foreground/10 py-4">
                       <p className="font-body text-ts-2xs font-semibold tracking-[0.25em] uppercase text-muted-foreground mb-3">
                         Recognition
                       </p>
                       <div className="flex flex-col gap-1">
                          {[
                        { key: "top-indie", text: <>Top Independent — <a href="https://on.contra.com/7z8Pwk" target="_blank" rel="noopener noreferrer" className="cs-inline-link">Contra</a></> },
                        { key: "top-expert", text: <>Top 1% Framer Expert — <a href="https://framer.link/keish-design-studio" target="_blank" rel="noopener noreferrer" className="cs-inline-link">Framer</a> & <a href="https://on.contra.com/7z8Pwk" target="_blank" rel="noopener noreferrer" className="cs-inline-link">Contra</a></> },
                        { key: "commerce", text: <>Framer Commerce Expert — <a href="https://framercommerce.com" target="_blank" rel="noopener noreferrer" className="cs-inline-link">Framer Commerce</a></> },
                        { key: "kajabi", text: <>Certified Expert — Kajabi</> }].
                        map((cred) =>
                        <p key={cred.key} className="font-body text-ts-2xs text-muted-foreground/70 tracking-wide">
                              {cred.text}
                            </p>
                        )}
                       </div>
                     </div>
                  </motion.div>
                </div>

                <ToolStack />

                {/* Mobile tertiary CTA */}
                 <button
                  onClick={() => goToSlide(1)}
                  className="flex lg:hidden items-center gap-2 mt-10 font-body text-ts-2xs tracking-[0.1em] uppercase text-muted-foreground hover:text-foreground transition-colors border-b border-dashed border-muted-foreground/40 hover:border-foreground/60 pb-0.5 group">

                  Meet the Founder
                  <ArrowUpRight size={12} className="group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform duration-300" />
                </button>

              </motion.div> :

              <motion.div
                key="madi"
                custom={direction}
                variants={isMobile ? fadeVariants : slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}>

                {/* ═══ SIDE B: Meet Madi ═══ */}
                <div className="grid lg:grid-cols-12 gap-y-6 lg:gap-x-8">
                   {/* Photo + Name — grouped as one unit on mobile, right column on desktop */}
                   <div className="order-first lg:order-last lg:col-span-5 lg:col-start-8 flex flex-col items-center lg:items-start gap-3 lg:sticky lg:top-24 lg:self-start">
                     <div className="relative w-40 lg:w-full max-w-[340px] aspect-square flex items-center justify-center">
                       <div className="w-full lg:w-[85%] aspect-square rounded-full overflow-hidden border-2 border-foreground/10 shadow-[0_8px_40px_-12px_hsl(var(--primary)/0.2)]">
                        <img
                          src={madiHeadshot}
                          alt="Madison Green"
                          className="w-full h-full object-cover" />

                      </div>
                      <a
                        href="https://www.linkedin.com/in/madigreen72/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="absolute bottom-[8%] right-[8%] lg:w-14 lg:h-14 w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center z-20 group shadow-lg hover:scale-110 transition-all duration-300 translate-y-3"
                        aria-label="View LinkedIn profile">

                         <ArrowUpRight
                          size={22}
                          className="group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform duration-300 lg:block hidden" />

                         <ArrowUpRight
                          size={16}
                          className="group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform duration-300 lg:hidden" />

                      </a>
                    </div>
                    {/* Name + role — mobile only, tightly coupled to photo */}
                    <div className="text-center lg:hidden">
                      <h2 className="font-display text-[clamp(2rem,7vw,3rem)] italic leading-[1.1] text-foreground/90">
                        Hey, I'm Madi.
                      </h2>
                      <p className="font-body text-ts-2xs tracking-[0.15em] uppercase text-muted-foreground/60 mt-1">
                         Founder & Lead Designer
                       </p>
                    </div>
                  </div>

                  {/* Left — personal intro */}
                   <div className="lg:col-span-6 lg:col-start-1">
                     <h2 className="hidden lg:block font-display text-[clamp(2.5rem,6vw,5rem)] italic leading-[1.15] text-foreground/90 mb-10">
                      Hey, I'm Madi.
                    </h2>
                     <div className="space-y-6">
                       <p className="font-body text-ts-xs leading-[2] text-muted-foreground">
                         I started KEISH because I kept running into the same problem: so many websites look great, but they're a pain in the ass to use. You have to think harder than you should just to get through something simple. Buttons don't respond the way you expect. Things move when they don't need to. It all looks impressive until you actually have to interact with it.
                       </p>
                       <p className="font-body text-ts-xs leading-[2] text-muted-foreground">
                         I've worked inside large product teams, Fortune 100 environments where things move slowly and deliberately, and with smaller teams moving fast and figuring things out in real time. Somewhere in the middle of that, it clicked how much of a product lives beneath the surface. Not the visuals, but the structure. How things are organized, how they respond, how they hold up once people start using them.
                       </p>
                       <p className="font-body text-ts-xs leading-[2] text-muted-foreground">
                         That's the part I tend to focus on. Making sure the logic makes sense. Fixing the pieces that get overlooked. Thinking through how something actually works, not just how it looks in a static frame.
                       </p>
                       <p className="font-body text-ts-xs leading-[2] text-muted-foreground">
                         A lot of that comes from my own life, too. I live with a kidney transplant, so I don't really have the option to ignore when something isn't intuitive or accessible. I notice when a product assumes too much, when it only works for a narrow group of people, or when it breaks if you don't move through it the "right" way. Once you start seeing that, it's hard to ignore.
                       </p>
                       <p className="font-body text-ts-xs leading-[2] text-muted-foreground">
                         So I don't.
                       </p>
                       <p className="font-body text-ts-xs leading-[2] text-muted-foreground">
                         At this point, I'm pretty intentional about who I work with. I like building for people who care about how their product actually functions once it's out in the world. Not just how it looks, but how it behaves when someone is really using it.
                       </p>
                    </div>

                    {/* Social links */}
                    <div className="flex items-center gap-3 mt-12">
                      <a href="https://www.linkedin.com/in/madigreen72/" target="_blank" rel="noopener noreferrer" className="p-2.5 border border-foreground/15 text-muted-foreground/60 hover:text-foreground hover:border-foreground/40 transition-colors duration-300" aria-label="LinkedIn">
                        <Linkedin size={16} />
                      </a>
                      <a href="https://www.instagram.com/keish.studio/" target="_blank" rel="noopener noreferrer" className="p-2.5 border border-foreground/15 text-muted-foreground/60 hover:text-foreground hover:border-foreground/40 transition-colors duration-300" aria-label="Instagram">
                        <Instagram size={16} />
                      </a>
                    </div>
                  </div>
                </div>

                {/* Mobile tertiary back link */}
                <button
                  onClick={() => goToSlide(0)}
                  className="flex lg:hidden items-center gap-2 mt-10 font-body text-[11px] tracking-[0.1em] uppercase text-muted-foreground hover:text-foreground transition-colors border-b border-dashed border-muted-foreground/40 hover:border-foreground/60 pb-0.5 group">

                  Back to Studio
                  <ArrowUpRight size={12} className="group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform duration-300" />
                </button>

              </motion.div>
              }
          </AnimatePresence>
        </div>
      </div>


      {/* ═══ Playbook sub-section ═══ */}
      <PlaybookSubSection />


      </div>
    </section>);

};

export default AboutSection;